import React, {
  useEffect, useState,
} from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getLocaleDateTime, getLocaleTime } from 'utils/time';
import { _t, formatFileSizeBinary, formatNumberSize } from 'utils';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  getColorInRange, splitId, getOpacity, getValueWithDecimalChar,
} from 'utils/metrics';
import { Button } from '@mui/material';
import { FilterLegend } from './components/FilterLegend';

export const DateBasedChart = ({
  bytes,
  percentage,
  data = [],
  hideLegend,
  legendLayout = 'horizontal',
  height = 38,
  filterable = false,
  maxYAxisValue,
  minMaxYAxisValue = 0,
  yLabel,
  xLabel,
}) => {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);

  let checkedData = data.filter((item) => item !== undefined && item !== null);

  const unfilteredCheckedData = checkedData;

  if (filter) {
    checkedData = checkedData.filter((item) => item.category.includes(filter));
  }

  useEffect(() => {
    if (checkedData.length === 0) return;
    const activeObject = checkedData.reduce((acc, item) => {
      acc[item.category] = true;
      return acc;
    }, {});
    setActive(activeObject);

    // eslint-disable-next-line
  }, [data.length]);

  let parsedData = {};
  let dataMax = minMaxYAxisValue;
  checkedData.forEach((parent) => {
    if (!parent.dataValues) return;
    parent.dataValues.forEach((value) => {
      if (!parsedData[value.date]) {
        parsedData[value.date] = {
          date: value.date,
        };
      }
      dataMax = Math.max(...[dataMax, value.value]);
      parsedData[value.date][parent.category] = value.value;
    });
  });
  if (dataMax > 1) {
    dataMax = 2 * Math.ceil(dataMax / 2);
  } else if (dataMax === 0) {
    dataMax = 1;
  } else {
    dataMax = Math.ceil(dataMax * 100) / 100;
  }

  parsedData = Object.values(parsedData)
    .sort((a, b) => a.date - b.date);

  const getShortenedName = (name = '', entry) => {
    if (!entry || !active) return splitId(name);
    const { dataKey } = entry;
    return (
      <span style={{ opacity: Math.min(getOpacity(active[dataKey]) + 0.3, 1) || 1 }}>{splitId(name)}</span>
    );
  };

  const getValue = ({
    value,
    round,
    format,
  }) => {
    if (bytes) return formatFileSizeBinary(value, round);
    if (percentage) return format ? `${value}%` : `${value.toFixed(2)}%`;
    return format ? formatNumberSize(value) : value.toFixed(2);
  };

  const renderLegend = ({ payload }) => (
    <FilterLegend
      active={active}
      setActive={setActive}
      height={height}
      legendLayout={legendLayout}
      checkedData={payload}
      filterable={filterable}
      getShortenedName={getShortenedName}
      scrollPosition={scrollPosition}
      setScrollPosition={setScrollPosition}
    />
  );

  const renderTooltip = ({
    active: itemActive,
    payload,
  }) => {
    if (itemActive && payload && payload.length) {
      const shortenedPayload = payload.filter((item) => active[item.dataKey])
        .slice(0, 10);
      return (
        <div className="customTooltipContainer legendContainer">
          {
            shortenedPayload.length && (
              <div className="tooltipTitle">{getLocaleDateTime({ date: shortenedPayload[0].payload.date })}</div>
            )
          }
          {
            shortenedPayload.map((item, idx) => (
              <div
                key={idx}
                className="legendItem"
              >
                <div className="legendSquare" style={{ backgroundColor: item.color }} />
                <span>
                  {getShortenedName(item.dataKey, item)}
                  {': '}
                  {getValueWithDecimalChar(getValue({
                    value: item.value,
                    round: false,
                    format: false,
                  }))}
                </span>
              </div>
            ))
          }
        </div>
      );
    }

    return null;
  };

  const getColorInRangeX = getColorInRange({
    color: '#5075a6',
    step: 30,
  });

  const onFilter = (event, value) => {
    if (filter !== (value || '')) {
      setFilter(value || '');
    }
  };

  const changeActive = (activeState) => {
    setActive(Object.keys(active)
      .reduce((acc, item) => {
        acc[item] = activeState;
        return acc;
      }, {}));
  };

  return (
    <div className="dcdChart">
      {
        filterable && (
          <div className="legendFilter">
            <div>
              <Autocomplete
                className="legendAutoComplete"
                freeSolo
                options={unfilteredCheckedData.map((option) => splitId(option.category))}
                renderInput={(params) => <TextField {...params} label="filter" onBlur={(e) => onFilter(e, e.target.value)} />}
                onChange={onFilter}
              />
            </div>
          </div>
        )
      }
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          width={500}
          height={300}
          data={parsedData}
          margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            label={xLabel && { value: xLabel, offset: 0, position: 'insideBottom' }}
            dataKey={checkedData.length ? 'date' : 'number'}
            tickFormatter={(item) => (item && item !== 'auto' ? getLocaleTime({ date: item }) : '')}
            tick={{ fontSize: 11 }}
            minTickGap={9}
            allowDataOverflow
          />
          <YAxis
            label={yLabel && { value: yLabel, angle: -90, position: 'insideLeft' }}
            width={!bytes ? 40 : 80}
            allowDecimals={dataMax <= 1}
            tickFormatter={(value) => getValueWithDecimalChar(getValue({
              value,
              round: true,
              format: true,
            }))}
            tick={{ fontSize: 11 }}

            tickLine={false}
            axisLine={false}
            domain={[0, maxYAxisValue || dataMax]}
            allowDataOverflow
          />
          {
            active && Object.values(active).some((item) => item) && (
              <Tooltip
                content={renderTooltip}
              />
            )
          }
          {
            !hideLegend && (
              <Legend
                formatter={getShortenedName}
                align="right"
                layout={legendLayout}
                content={renderLegend}
                width={filterable ? 260 : undefined}
              />
            )
          }
          {
            checkedData.map((item, idx) => (
              <Area
                key={idx}
                type="monotone"
                strokeOpacity={getOpacity(active?.[item.category])}
                fillOpacity={active?.[item.category] ? 0.4 : getOpacity(active?.[item.category])}
                dataKey={item.category}
                stroke={getColorInRangeX(idx)}
                fill={getColorInRangeX(idx)}
              />
            ))
          }
        </AreaChart>
      </ResponsiveContainer>

      {
        filterable && (
          <div className="legendActionButtons">
            <div className="actionButtons">
              <Button
                onClick={() => changeActive(true)}
                color="primary"
              >
                {_t('Select all')}
              </Button>
              <Button
                onClick={() => changeActive(false)}
                color="primary"
              >
                {_t('Deselect all')}
              </Button>
            </div>
          </div>
        )
      }
    </div>
  );
};
